// The pieces of MUI's `Grow` that the ported surfaces need and cannot express
// in CSS alone — shared by the menu paper (`icon-menu-button.tsx`), the
// popover paper (`mui-popover.tsx`) and the tooltip box (`mui-tooltip.tsx`),
// which run the same `mui-grow-*` keyframes on their own clocks.

import { MUI_POPOVER_MARGIN_THRESHOLD as MARGIN_THRESHOLD } from "@/components/ui/mui-field-shape";
import type { Horizontal, MuiPopoverOrigin, Vertical } from "@/components/ui/mui-popover";
import { autoHeightDuration } from "@/components/ui/mui-transitions";

/**
 * Set a paper's Grow clock at `timeout='auto'` — what a `Menu` and a `Popover`
 * give their `Grow` (`Menu.js:79,181`, `Popover.js`): the duration from the
 * paper's height (`getAutoHeightDuration`, so a taller paper opens more
 * slowly), the transform over two thirds of it, and — exit only — the
 * transform held back a third. `Grow.js:140-147` gives the transform arm
 * `transitionTiming.delay || duration * 0.333`, and the enter side's delay is
 * 0, so the two directions share the two durations and differ in the delay.
 */
export function setGrowClock(node: HTMLElement) {
  const duration = autoHeightDuration(node.clientHeight);
  node.style.setProperty("--mui-menu-grow-duration", `${duration}ms`);
  node.style.setProperty("--mui-menu-grow-transform-duration", `${Math.round(duration * 0.666)}ms`);
  node.style.setProperty("--mui-menu-grow-exit-delay", `${Math.round(duration * 0.333)}ms`);
}

/**
 * Pin where the exit should start from — **the size the box had actually
 * reached**, not full size.
 *
 * `Grow` writes a CSS *transition* and then sets the target
 * (`Grow.js:140-152`), so an exit interpolates from the element's current
 * computed state: close a donor menu while it is still opening and it shrinks
 * from wherever it had got to. A CSS animation cannot do that on its own — its
 * `from` frame is a constant, and the underlying value the browser would use
 * instead reverts to the base style the moment the entering animation is
 * replaced. So the live value is read here and handed to the keyframes as a
 * custom property.
 *
 * Read *before* the state flips, which is what decides where each caller calls
 * it from: the menu's own `setOpen` on the root, and the tooltip's
 * `onOpenChange`, both of which run in the event that closes rather than in the
 * render that follows. Once `data-state` says closed the exit animation is
 * already what `getComputedStyle` reports, and the live value is gone. A close
 * a prop change raises rather than an event has no such moment, so its caller
 * pins from a layout effect on the commit *before* the one that closes — see
 * the tooltip's empty-title edge.
 *
 * An animation, not a transition, because Radix decides when to unmount from
 * `animationend` (`react-presence/dist/index.mjs:73-104`) — a transition would
 * take the box out from under the motion.
 */
export function pinExitStart(node: HTMLElement | null) {
  if (!node) return;
  const { transform, opacity } = getComputedStyle(node);
  node.style.setProperty("--mui-grow-exit-from-transform", transform);
  node.style.setProperty("--mui-grow-exit-from-opacity", opacity);
}

/**
 * The same, in the other direction — where an *exit* should be grown out of.
 *
 * `Grow` is symmetric, because a transition is: `handleEnter` writes a fresh
 * transition and lets React apply `styles.entering` (`Grow.js:75-113,23-31`)
 * without resetting the element first, so re-opening a box that is still
 * closing carries on from the size the exit had reached, exactly as
 * interrupting an open shrinks from the size the enter had reached. A CSS
 * animation's `from` frame is a constant, so without this the box jumps back
 * to `scale(0.75, 0.5625)` and replays the whole enter.
 *
 * Read from the same moment and for the same reason as `pinExitStart`: before
 * the state flips, while `data-state` still says closed and the exit is still
 * what `getComputedStyle` reports. A caller with nothing mounted passes null
 * and the keyframes fall back to their own `from`, which is the right answer
 * for a box that is genuinely starting from nothing.
 */
export function pinEnterStart(node: HTMLElement | null) {
  if (!node) return;
  const { transform, opacity } = getComputedStyle(node);
  node.style.setProperty("--mui-grow-enter-from-transform", transform);
  node.style.setProperty("--mui-grow-enter-from-opacity", opacity);
}

/** How far along an edge of `size` an origin's point sits. */
export const along = (point: Vertical | Horizontal, size: number) =>
  point === "top" || point === "left" ? 0 : point === "center" ? size / 2 : size;

/**
 * MUI's clamp, on a paper Radix has placed — `MuiPopover`'s, and the Spec
 * list's division menu, which is a `Select`'s `Popover` too (`Popover.js:189-222`).
 *
 * MUI computes where the paper would sit — the anchor point less the origin
 * point — and pushes it back inside `marginThreshold` along each axis in turn,
 * adding what it moved to the transform origin so the Grow still unfolds from
 * the anchor. Radix's `shift` already does the horizontal push; the vertical it
 * leaves to `flip`, which this paper is kept from. So the horizontal overflow is
 * only *measured* here, for the origin, and the vertical one is also *applied*
 * — through `translate`, which neither Radix's positioning (`transform` on the
 * wrapper) nor the Grow (`transform` on the paper) writes, and which, unlike a
 * margin, moves a paper that opens upward too.
 *
 * Every input is readable before the first paint: the trigger's box and the
 * paper's layout size (`offset*`, not the rect, which the Grow's opening scale
 * has already shrunk). So this runs in the ref callback, and there is no frame
 * with the paper hanging off the window.
 */
export function clampIntoView(
  node: HTMLElement,
  trigger: HTMLElement | null,
  anchor: MuiPopoverOrigin,
  transform: MuiPopoverOrigin,
) {
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const origin = {
    x: along(transform.horizontal, node.offsetWidth),
    y: along(transform.vertical, node.offsetHeight),
  };
  const top = rect.top + along(anchor.vertical, rect.height) - origin.y;
  const left = rect.left + along(anchor.horizontal, rect.width) - origin.x;
  const overflow = (start: number, size: number, room: number) =>
    start < MARGIN_THRESHOLD
      ? start - MARGIN_THRESHOLD
      : start + size > room - MARGIN_THRESHOLD
        ? start + size - (room - MARGIN_THRESHOLD)
        : 0;
  const dy = overflow(top, node.offsetHeight, window.innerHeight);
  const dx = overflow(left, node.offsetWidth, window.innerWidth);
  // Cleared, not left behind, when the room comes back: MUI derives both from
  // scratch on every placement.
  node.style.translate = dy === 0 ? "" : `0 ${-dy}px`;
  node.style.transformOrigin = `${origin.x + dx}px ${origin.y + dy}px`;
}
